import React, { ReactElement, useEffect, useState } from "react";
import { Alert, Pagination } from "react-bootstrap";
import ReactDom from "react-dom";
import { RouteComponentProps } from "react-router";
import { DataTable } from "../ui/DataTable";
import { ProductService } from "./product/ProductService";

interface IParams {
	id?: string;
}

interface IAccountProduct {
	id: number;
	code: string;
	name: string;
	first_purchase_date: string;
	purchase_count: number;
	recent_opportunity_id: number | null;
	recent_opportunity_name: string | null;
	bundle: string;
}

interface IPaginatedResponse {
	data: IAccountProduct[];
	current_page: number;
	last_page: number;
	total: number;
	per_page: number;
}

export const AccountProductsTable = (
	props: RouteComponentProps<IParams>,
): ReactElement => {
	const [paginatedData, setPaginatedData] = useState<IPaginatedResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage] = useState(15);
	const portal = document.getElementById("account-products-portal");

	if (!portal) {
		throw Error("#account-products-portal element could not be found");
	}

	const productService = new ProductService();

	const columns = [
		{
			Header: "Product Code",
			accessor: "code",
			width: "15%",
		},
		{
			Header: "Product Name",
			accessor: "name",
			width: "35%",
		},
		{
			Header: "Bundle",
			accessor: "bundle",
			width: "15%",
		},
		{
			Header: "First Purchase",
			accessor: "first_purchase_date",
			Cell: ({ cell: { value } }: any) => {
				if (!value) return "-";
				const date = new Date(value);
				return date.toLocaleDateString();
			},
			width: "15%",
		},
		{
			Header: "Times Purchased",
			accessor: "purchase_count",
			width: "10%",
		},
		{
			Header: "Recent Opportunity",
			accessor: "recent_opportunity_name",
			Cell: ({ row: { original } }: any) => {
				if (!original.recent_opportunity_id) return "-";
				return (
					<a href={`/opportunities/${original.recent_opportunity_id}`}>
						{original.recent_opportunity_name}
					</a>
				);
			},
			width: "10%",
		},
	];

	useEffect(() => {
		const fetchProducts = async () => {
			setLoading(true);
			try {
				const data = await productService.findByAccount(
					props.match.params.id || "",
					currentPage,
					perPage
				);
				setPaginatedData(data);
			} catch (error) {
				console.error("Error fetching account products:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchProducts();
	}, [props.match.params.id, currentPage, perPage]);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const renderPagination = () => {
		if (!paginatedData || paginatedData.last_page <= 1) return null;

		const items = [];
		const { current_page, last_page } = paginatedData;

		// Previous button
		items.push(
			<Pagination.Prev
				key="prev"
				disabled={current_page === 1}
				onClick={() => handlePageChange(current_page - 1)}
			/>
		);

		// Page numbers
		const startPage = Math.max(1, current_page - 2);
		const endPage = Math.min(last_page, current_page + 2);

		for (let page = startPage; page <= endPage; page++) {
			items.push(
				<Pagination.Item
					key={page}
					active={page === current_page}
					onClick={() => handlePageChange(page)}
				>
					{page}
				</Pagination.Item>
			);
		}

		// Next button
		items.push(
			<Pagination.Next
				key="next"
				disabled={current_page === last_page}
				onClick={() => handlePageChange(current_page + 1)}
			/>
		);

		return <Pagination className="justify-content-center mt-3">{items}</Pagination>;
	};

	let output;

	if (loading) {
		output = (
			<Alert variant="info">
				Loading products...
			</Alert>
		);
	} else if (!paginatedData || !paginatedData.data || paginatedData.data.length === 0) {
		output = (
			<Alert variant="info">
				No products have been purchased by this account.
			</Alert>
		);
	} else {
		output = (
			<>
				<div className="mb-3">
					<small className="text-muted">
						Showing {paginatedData.data.length} of {paginatedData.total} products
					</small>
				</div>
				<DataTable columns={columns} data={paginatedData.data} />
				{renderPagination()}
			</>
		);
	}

	return ReactDom.createPortal(output, portal);
};